import { useState, useEffect, useCallback, useRef } from 'react';

const useInfiniteScroll = (fetchFunction, initialData = null) => {
  const [data, setData] = useState(initialData?.results || []);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [count, setCount] = useState(initialData?.count || 0);
  const [next, setNext] = useState(initialData?.next || null);
  
  const observerRef = useRef();
  const loadingRef = useRef(false);
  const observerRootRef = useRef(null);
  const userScrolledRef = useRef(false);

  // Función para cargar más datos
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore || !next) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await fetchFunction(next);
      
      if (response && response.data) {
        const newData = response.data.results || [];
        
        setData(prevData => {
          // Evitar duplicados comparando por ID
          const existingIds = new Set(prevData.map(item => item.id));
          const filteredNewData = newData.filter(item => !existingIds.has(item.id));
          return [...prevData, ...filteredNewData];
        });
        
        setCount(response.data.count || 0);
        setNext(response.data.next);
        setHasMore(!!response.data.next);
      }
    } catch (err) {
      console.error('Error loading more data:', err);
      setError(err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [fetchFunction, next, hasMore]);

  // Función para resetear los datos (útil para nuevas búsquedas)
  const resetData = useCallback((newInitialData = null) => {
    setData(newInitialData?.results || []);
    setCount(newInitialData?.count || 0);
    setNext(newInitialData?.next || null);
    setHasMore(!!newInitialData?.next);
    setError(null);
    setLoading(false);
    loadingRef.current = false;
  }, []);

  // Fusionar actualizaciones en los items existentes por id (útil para progreso/estado)
  const updateItems = useCallback((updatedItems = []) => {
    if (!Array.isArray(updatedItems) || updatedItems.length === 0) return;
    setData(prevData => {
      const map = new Map(prevData.map(item => [item.id, item]));
      for (const upd of updatedItems) {
        if (upd && typeof upd.id !== 'undefined' && map.has(upd.id)) {
          map.set(upd.id, { ...map.get(upd.id), ...upd });
        }
      }
      return Array.from(map.values());
    });
  }, []);

  // Configurar el observer para detectar cuando el usuario llega al final
  const lastElementRef = useCallback((node) => {
    if (loading) return;
    
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      const entry = entries[0];
      // Cargar más cuando el sentinel esté prácticamente al 100% visible dentro del contenedor
      if (!userScrolledRef.current) return;
      if (entry.isIntersecting && entry.intersectionRatio >= 0.99 && hasMore && !loadingRef.current) {
        loadMore();
      }
    }, {
      threshold: 0.99,
      rootMargin: '0px',
      root: observerRootRef.current || null
    });
    
    if (node) observerRef.current.observe(node);
  }, [loading, hasMore, loadMore]);

  // Limpiar el observer al desmontar
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    data,
    loading,
    hasMore,
    error,
    count,
    loadMore,
    resetData,
    lastElementRef,
    updateItems,
    setObserverRoot: (node) => { observerRootRef.current = node; },
    setUserScrolled: (flag = true) => { userScrolledRef.current = flag; }
  };
};

export default useInfiniteScroll;